import { useState, useEffect, useRef } from "react";
import { stockReceiptApi, stockReceiptDtlApi, vendorApi, productApi, inventoryItemDtlApi } from "../api/api";
import { emitDataMutation } from "../utils/appEvents";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import MasterDetailView from "../components/MasterDetailView";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";

/* ── Toast ── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ── Confirm Dialog ── */
function ConfirmDialog({ name, onConfirm, onCancel }) {
  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 500 }}>
      <div className="rv-dialog">
        <div className="rv-dialog-icon"><TrashIcon /></div>
        <h3 className="rv-dialog-title">Delete Stock Receipt?</h3>
        <p className="rv-dialog-desc">
          <strong>{name}</strong> will be permanently removed. This action cannot be undone.
        </p>
        <div className="rv-dialog-actions">
          <button className="rv-btn rv-btn--ghost" onClick={onCancel}>Cancel</button>
          <button className="rv-btn rv-btn--danger" onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Stock Receipt Form Modal ── */
function StockReceiptModal({ initial, vendors, products, onSave, onClose, saving }) {
  const isEdit = !!initial?.receiptID;
  const todayISO = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    vendorID: initial?.vendorID ? String(initial.vendorID) : "",
    receiptDate: initial?.receiptDate ? initial.receiptDate.split("T")[0] : todayISO,
    comments: initial?.comments ?? "",
  });

  const [lines, setLines] = useState(() =>
    initial?.lines && initial.lines.length > 0
      ? initial.lines.map(l => ({
        receiptLineID: l.receiptLineID ?? null,
        productID: l.productID != null ? String(l.productID) : "",
        quantity: l.quantity != null ? String(l.quantity) : "",
        unitWeight: l.unitWeight != null ? String(l.unitWeight) : "",
        totalWeight: l.totalWeight != null ? String(l.totalWeight) : "",
        comments: l.comments ?? "",
      }))
      : [{ receiptLineID: null, productID: "", quantity: "", unitWeight: "", totalWeight: "", comments: "" }]
  );

  const [errors, setErrors] = useState({});
  const [lineErrors, setLineErrors] = useState([]);

  const updateLine = (idx, key, val) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [key]: val } : l));
    setLineErrors(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], [key]: "" };
      return next;
    });
  };

  const addLine = () =>
    setLines(prev => [...prev, { receiptLineID: null, productID: "", quantity: "", unitWeight: "", totalWeight: "", comments: "" }]);

  const removeLine = (idx) =>
    setLines(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!form.vendorID) e.vendorID = "Vendor is required.";
    if (!form.receiptDate) e.receiptDate = "Receipt date is required.";

    const le = lines.map(l => {
      const err = {};
      if (!l.productID) err.productID = "Required.";
      if (!l.quantity || isNaN(Number(l.quantity)) || Number(l.quantity) <= 0)
        err.quantity = "Required.";
      return err;
    });
    setLineErrors(le);

    const linesValid = le.every(e => Object.keys(e).length === 0);
    if (lines.length === 0) e.lines = "At least one line item is required.";
    return linesValid && Object.keys(e).length === 0 ? null : e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (e) { setErrors(e); return; }
    onSave({ ...form, lines });
  };

  return (
    <div className="rv-dialog-backdrop">
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 740, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><ReceiptIcon /></div>
          <div>
            <h3 className="rv-modal-title">{isEdit ? "Edit Stock Receipt" : "New Stock Receipt"}</h3>
            <p className="rv-modal-sub">{isEdit ? "Update receipt details." : "Record a new vendor delivery."}</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body" style={{ overflowY: "auto", maxHeight: "65vh" }}>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Vendor <span className="rv-required">*</span></label>
              <SearchableDropdown
                options={vendors}
                valueKey="vendorID"
                labelKey="vendorName"
                value={form.vendorID}
                onChange={val => {
                  setForm(f => ({ ...f, vendorID: val }));
                  setErrors(v => ({ ...v, vendorID: "" }));
                }}
                placeholder="— Select vendor —"
                searchPlaceholder="Search vendors…"
                error={errors.vendorID}
                pageSize={10}
              />
            </div>
            <div className="rv-field">
              <label className="rv-label">Receipt Date <span className="rv-required">*</span></label>
              <input
                className={`rv-input rv-input--date ${errors.receiptDate ? "rv-input--err" : ""}`}
                type="date"
                value={form.receiptDate}
                onChange={e => { setForm(f => ({ ...f, receiptDate: e.target.value })); setErrors(v => ({ ...v, receiptDate: "" })); }}
              />
              {errors.receiptDate && <p className="rv-field-err">{errors.receiptDate}</p>}
            </div>
          </div>

          <div className="rv-field">
            <label className="rv-label">Comments</label>
            <textarea
              className="rv-input"
              rows={2}
              placeholder="Optional notes…"
              value={form.comments}
              maxLength={500}
              onChange={e => setForm(f => ({ ...f, comments: e.target.value }))}
              style={{ resize: "vertical" }}
            />

            <p style={{ fontSize: 11, color: "var(--taupe)", textAlign: "right", marginTop: 2 }}>
              {form.comments.length}/500
            </p>
          </div>

          <div className="rv-field">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <label className="rv-label" style={{ marginBottom: 0 }}>Line Items <span className="rv-required">*</span></label>
              <button className="rv-btn rv-btn--ghost" style={{ padding: "4px 10px", fontSize: 12 }} onClick={addLine}>
                <PlusIcon /> Add Line
              </button>
            </div>

            {errors.lines && <p className="rv-field-err" style={{ marginBottom: 8 }}>{errors.lines}</p>}

            {lines.map((line, idx) => (
              <div key={idx} style={{ border: "1px solid rgba(185,167,146,.2)", borderRadius: 10, padding: "12px 14px", marginBottom: 10, background: "rgba(185,167,146,.04)" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", textTransform: "uppercase", letterSpacing: ".5px" }}>Line {idx + 1}</span>
                  {lines.length > 1 && (
                    <button
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#c0392b", padding: 4, display: "flex", alignItems: "center" }}
                      onClick={() => removeLine(idx)}
                      title="Remove line"
                    ><TrashIcon /></button>
                  )}
                </div>

                <div className="rv-field-row-2col" style={{ marginBottom: 8 }}>
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label">Product <span className="rv-required">*</span></label>
                    <SearchableDropdown
                      options={products}
                      valueKey="productID"
                      labelKey="productName"
                      value={line.productID}
                      onChange={val => updateLine(idx, "productID", val)}
                      placeholder="— Select product —"
                      searchPlaceholder="Search products…"
                      error={lineErrors[idx]?.productID}
                      pageSize={10}
                    />
                  </div>
                  <div className="rv-field" style={{ marginBottom: 0 }}>
                    <label className="rv-label">Qty Received <span className="rv-required">*</span></label>
                    <input
                      className={`rv-input ${lineErrors[idx]?.quantity ? "rv-input--err" : ""}`}
                      type="number" min="1" placeholder="0"
                      value={line.quantity}
                      onChange={e => updateLine(idx, "quantity", e.target.value)}
                    />
                    {lineErrors[idx]?.quantity && <p className="rv-field-err">{lineErrors[idx].quantity}</p>}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                  {[

                    ["totalWeight", "Total Weight (g)"],
                  ].map(([key, label]) => (
                    <div className="rv-field" key={key} style={{ marginBottom: 0 }}>
                      <label className="rv-label">{label}</label>
                      <input
                        className="rv-input"
                        type="number" min="0" step="0.001" placeholder="0.000"
                        value={line[key]}
                        onChange={e => updateLine(idx, key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <div className="rv-field" style={{ marginBottom: 0 }}>
                  <label className="rv-label">Line Comments</label>
                  <input
                    className="rv-input"
                    type="text" placeholder="Optional…"
                    value={line.comments}
                    onChange={e => updateLine(idx, "comments", e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>

        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleSubmit} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : null}
            {isEdit ? "Update" : "Save Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Operational Hub Nav ── */
function HubNav({ active = "Stock Receipt" }) {
  const navTo = (target) => {
    window.dispatchEvent(new CustomEvent("reva:navigate", { detail: target }));
  };

  return (
    <div className="rv-hub-nav">
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stock Receipt" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stock Receipt")}
      >
        <ReceiptIcon /> Vendor Inward (Stock Receipts)
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Stocks" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Stocks")}
      >
        <TagIcon /> Jewelry Items & Barcodes
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Bulk Stock In" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Bulk Stock In")}
      >
        <BoxIcon /> Stock Balances
      </button>
      <button
        type="button"
        className={`rv-hub-tab ${active === "Inventory Transaction" ? "rv-hub-tab--active" : ""}`}
        onClick={() => navTo("Inventory Transaction")}
      >
        <TxnIcon /> Movement Ledger
      </button>
    </div>
  );
}

/* ── Tag Generation Modal ── */
function TagGenerationModal({ line, product, receipt, vendorName, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    grossWeight: line.totalWeight != null ? String(line.totalWeight) : "",
    lessWeight: "0",
    netWeight: line.totalWeight != null ? String(line.totalWeight) : "",
    quantity: line.quantity != null ? String(line.quantity) : "1",
  });
  const [errors, setErrors] = useState({});

  const updateField = (key, val) => {
    setForm(prev => {
      const updated = { ...prev, [key]: val };
      if (key === "grossWeight" || key === "lessWeight") {
        const g = parseFloat(key === "grossWeight" ? val : updated.grossWeight) || 0;
        const l = parseFloat(key === "lessWeight" ? val : updated.lessWeight) || 0;
        updated.netWeight = Math.max(0, parseFloat((g - l).toFixed(3)));
      }
      return updated;
    });
    setErrors(e => ({ ...e, [key]: "" }));
  };

  const handleConfirm = () => {
    const e = {};
    if (!form.grossWeight || isNaN(Number(form.grossWeight)) || Number(form.grossWeight) <= 0) {
      e.grossWeight = "Valid gross weight is required.";
    }
    if (!form.quantity || isNaN(Number(form.quantity)) || Number(form.quantity) <= 0) {
      e.quantity = "Valid quantity is required.";
    }
    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }
    onSave({
      receiptLineID: Number(line.receiptLineID),
      productID: Number(line.productID),
      grossWeight: parseFloat(form.grossWeight) || 0,
      lessWeight: parseFloat(form.lessWeight) || 0,
      netWeight: parseFloat(form.netWeight) || 0,
      quantity: Number(form.quantity) || 1,
      barcodeID: 0,
    });
  };

  return (
    <div className="rv-dialog-backdrop" style={{ zIndex: 10000 }}>
      <div className="rv-dialog rv-dialog--form" style={{ maxWidth: 520, width: "100%" }}>
        <div className="rv-modal-header">
          <div className="rv-modal-icon"><TagIcon /></div>
          <div>
            <h3 className="rv-modal-title">Generate Jewelry Inventory Tag</h3>
            <p className="rv-modal-sub">Create stock item record & print barcode for received consignment.</p>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        <div className="rv-modal-body">
          <div className="rv-tag-modal-banner">
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--burgundy)", textTransform: "uppercase" }}>
                Consignment Item
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "var(--dark)", marginTop: 2 }}>
                {product?.productName || `Product #${line.productID}`}
              </div>
              <div style={{ fontSize: 12, color: "var(--taupe)", marginTop: 2 }}>
                Vendor: <strong>{vendorName}</strong> · Receipt #{receipt.receiptID}
              </div>
            </div>
            {product?.purityStandard && (
              <span style={{ fontSize: 12, fontWeight: 700, color: "var(--burgundy-dark)", background: "white", padding: "4px 10px", borderRadius: 6, border: "1px solid rgba(185,167,146,.25)" }}>
                {product.purityStandard}
              </span>
            )}
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Gross Weight (g) <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.grossWeight ? "rv-input--err" : ""}`}
                type="number" step="0.001" min="0"
                value={form.grossWeight}
                onChange={e => updateField("grossWeight", e.target.value)}
              />
              {errors.grossWeight && <p className="rv-field-err">{errors.grossWeight}</p>}
            </div>
            <div className="rv-field">
              <label className="rv-label">Less Weight (g)</label>
              <input
                className="rv-input"
                type="number" step="0.001" min="0"
                placeholder="0.000"
                value={form.lessWeight}
                onChange={e => updateField("lessWeight", e.target.value)}
              />
              <p className="rv-field-hint">Stone/Wax tare weight</p>
            </div>
          </div>

          <div className="rv-field-row-2col">
            <div className="rv-field">
              <label className="rv-label">Net Metal Weight (g)</label>
              <input
                className="rv-input"
                style={{ fontWeight: 700, color: "var(--burgundy-dark)", background: "rgba(104,26,45,.04)" }}
                type="number" step="0.001" readOnly
                value={form.netWeight}
              />
            </div>
            <div className="rv-field">
              <label className="rv-label">Quantity <span className="rv-required">*</span></label>
              <input
                className={`rv-input ${errors.quantity ? "rv-input--err" : ""}`}
                type="number" min="1"
                value={form.quantity}
                onChange={e => updateField("quantity", e.target.value)}
              />
              {errors.quantity && <p className="rv-field-err">{errors.quantity}</p>}
            </div>
          </div>
        </div>

        <div className="rv-modal-footer">
          <button className="rv-btn rv-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="rv-btn rv-btn--primary" onClick={handleConfirm} disabled={saving}>
            {saving ? <span className="rv-spinner" /> : <SparklesIcon />}
            {saving ? "Generating Tag…" : "Generate & Print Tag"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Stock Receipt Master-Detail View ── */
function StockReceiptDetailView({
  receipt,
  vendors,
  products,
  allDetails,
  inventoryItems = [],
  onBack,
  onEdit,
  onPrintTag,
  onGenerateTag,
  onPrintBatch,
  onAutoTagAll,
}) {
  useEffect(() => {
    const el = document.querySelector(".rv-content");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [receipt.receiptID]);

  const vendor = vendors.find(v => v.vendorID === receipt.vendorID);
  const vendorName = vendor?.vendorName ?? `Vendor #${receipt.vendorID}`;
  const lines = allDetails.filter(d => d.receiptID === receipt.receiptID);

  const [selectedLineId, setSelectedLineId] = useState(lines[0]?.receiptLineID ?? 0);

  const fmtDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtW = (v) => (v != null && v !== "" && !isNaN(v)) ? `${parseFloat(v).toFixed(3)} g` : "—";

  const totalQty = lines.reduce((acc, l) => acc + (Number(l.quantity) || 0), 0);
  const totalWeight = lines.reduce((acc, l) => acc + (parseFloat(l.totalWeight) || 0), 0);

  const selectedLine = lines.find(l => (l.receiptLineID ?? 0) === selectedLineId) || lines[0];

  // Tag status calculations
  const receiptTaggedItems = inventoryItems.filter(i =>
    lines.some(l => Number(l.receiptLineID) === Number(i.receiptLineID))
  );
  const untaggedLines = lines.filter(l =>
    !inventoryItems.some(i => Number(i.receiptLineID) === Number(l.receiptLineID))
  );

  return (
    <MasterDetailView
      title={`Stock Receipt #${receipt.receiptID}`}
      subtitle={`${vendorName} · ${fmtDate(receipt.receiptDate)}`}
      badgeText={`${lines.length} Line Item${lines.length === 1 ? "" : "s"} · ${receiptTaggedItems.length} Tagged`}
      onBack={onBack}
      backLabel="Back to Receipts"
      timelineTitle={`Line Items (${lines.length})`}
      items={lines}
      selectedItem={selectedLine}
      onSelectItem={(item) => setSelectedLineId(item.receiptLineID ?? 0)}
      extraHeaderActions={
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {receiptTaggedItems.length > 0 && (
            <button
              className="rv-action-btn--print-tag"
              style={{ padding: "8px 14px", fontSize: 13 }}
              onClick={() => onPrintBatch(receiptTaggedItems)}
              title="Print jewelry tags for all tagged items in this receipt"
            >
              <PrintIcon /> Print All Tags ({receiptTaggedItems.length})
            </button>
          )}
          {untaggedLines.length > 0 && (
            <button
              className="rv-action-btn--generate-tag"
              style={{ padding: "8px 14px", fontSize: 13 }}
              onClick={() => onAutoTagAll(lines, receipt)}
              title="Auto-create inventory items & tags for untagged lines"
            >
              <SparklesIcon /> Auto-Tag All ({untaggedLines.length})
            </button>
          )}
          <button
            className="rv-btn rv-btn--primary"
            style={{ padding: "8px 16px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}
            onClick={() => onEdit(receipt)}
          >
            <EditIcon /> Edit Receipt
          </button>
        </div>
      }
      renderTimelineItem={(line, idx, isSelected) => {
        const prod = products.find(p => p.productID === line.productID);
        const lineTagged = inventoryItems.find(i => Number(i.receiptLineID) === Number(line.receiptLineID));

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "var(--burgundy-dark)" : "var(--dark)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {prod?.productName ?? `Product #${line.productID}`}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--taupe)", background: "rgba(185,167,146,.15)", padding: "2px 6px", borderRadius: 4 }}>
                #{idx + 1}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--taupe)" }}>
              <span>Qty: <strong style={{ color: "var(--dark)" }}>{line.quantity ?? "—"}</strong></span>
              <span>Total Wt: <strong style={{ color: "var(--dark)" }}>{fmtW(line.totalWeight)}</strong></span>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, marginTop: 2 }}>
              {lineTagged ? (
                <span style={{ fontSize: 11, fontWeight: 700, color: "#2e7d32", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <CheckIcon /> Tagged (#{lineTagged.inventoryItemID})
                </span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 600, color: "#e65100" }}>
                  ⏳ Pending Tag
                </span>
              )}
            </div>

            {line.comments && (
              <p style={{ margin: 0, fontSize: 11.5, color: "var(--taupe-dark)", fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                "{line.comments}"
              </p>
            )}
          </div>
        );
      }}
      renderDetail={(line) => {
        if (!line) {
          return (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "var(--taupe)" }}>
              <p>No line items found for this receipt.</p>
            </div>
          );
        }

        const lineProd = products.find(p => p.productID === line.productID);
        const lineTagged = inventoryItems.find(i => Number(i.receiptLineID) === Number(line.receiptLineID));

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Header summary banner */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 14,
              padding: "16px 20px",
              background: "linear-gradient(135deg, rgba(104,26,45,.06) 0%, rgba(185,167,146,.08) 100%)",
              borderRadius: 12,
              border: "1px solid rgba(104,26,45,.12)"
            }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".5px", color: "var(--burgundy)" }}>
                  Product Details
                </div>
                <h3 style={{ margin: "3px 0 0 0", fontSize: 20, color: "var(--burgundy-dark)", fontWeight: 700 }}>
                  {lineProd?.productName ?? `Product #${line.productID}`}
                </h3>
                {lineProd?.productCode && (
                  <span style={{ fontSize: 12, color: "var(--taupe)", marginTop: 2, display: "inline-block" }}>
                    SKU / Code: <strong>{lineProd.productCode}</strong>
                  </span>
                )}
              </div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ background: "white", padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(185,167,146,.2)", textAlign: "center" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", color: "var(--taupe)", fontWeight: 700 }}>Receipt Total Qty</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--dark)" }}>{totalQty} pcs</div>
                </div>
                <div style={{ background: "white", padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(185,167,146,.2)", textAlign: "center" }}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", color: "var(--taupe)", fontWeight: 700 }}>Receipt Total Wt</div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "var(--burgundy)" }}>{totalWeight.toFixed(3)} g</div>
                </div>
              </div>
            </div>

            {/* Jewelry Barcode Tag Status & Actions */}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 14,
              padding: "16px 20px",
              background: lineTagged ? "rgba(46, 125, 50, 0.05)" : "rgba(230, 81, 0, 0.05)",
              border: lineTagged ? "1.5px solid rgba(46, 125, 50, 0.25)" : "1.5px dashed rgba(230, 81, 0, 0.35)",
              borderRadius: 12
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 10,
                  background: lineTagged ? "rgba(46, 125, 50, 0.12)" : "rgba(230, 81, 0, 0.12)",
                  color: lineTagged ? "#2e7d32" : "#e65100",
                  display: "flex", alignItems: "center", justifyContent: "center"
                }}>
                  <TagIcon />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--dark)" }}>
                      {lineTagged ? `Inventory Item #${lineTagged.inventoryItemID}` : "Consignment Barcode Tag"}
                    </h4>
                    <span className={`rv-tag-badge ${lineTagged ? "rv-tag-badge--tagged" : "rv-tag-badge--pending"}`}>
                      {lineTagged ? "✓ Tagged & Barcoded" : "Pending Tag"}
                    </span>
                  </div>
                  <p style={{ margin: "3px 0 0 0", fontSize: 12.5, color: "var(--taupe)" }}>
                    {lineTagged
                      ? `Barcode ID #${lineTagged.barcodeID || lineTagged.inventoryItemID} · Gross: ${lineTagged.grossWeight}g · Net: ${lineTagged.netWeight}g`
                      : "Create stock record & print Code128 jewelry hang tags for this consignment item."
                    }
                  </p>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {lineTagged ? (
                  <button
                    className="rv-action-btn--print-tag"
                    onClick={() => onPrintTag(lineTagged, lineProd)}
                  >
                    <PrintIcon /> Print Barcode Tag
                  </button>
                ) : (
                  <button
                    className="rv-action-btn--generate-tag"
                    onClick={() => onGenerateTag(line, lineProd, receipt)}
                  >
                    <SparklesIcon /> Generate Tag & Print
                  </button>
                )}
              </div>
            </div>

            {/* Line Item Metrics Cards */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Item Metrics & Weights
              </h4>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
                <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: "14px 16px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--taupe)", display: "block" }}>
                    Received Quantity
                  </span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", display: "block", marginTop: 4 }}>
                    {line.quantity ?? "—"} <span style={{ fontSize: 13, fontWeight: 500, color: "var(--taupe)" }}>pcs</span>
                  </span>
                </div>

                <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: "14px 16px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--taupe)", display: "block" }}>
                    Unit Weight (Per Piece)
                  </span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "var(--dark)", display: "block", marginTop: 4 }}>
                    {fmtW(line.unitWeight)}
                  </span>
                </div>

                <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 10, padding: "14px 16px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--taupe)", display: "block" }}>
                    Total Line Weight
                  </span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: "var(--burgundy)", display: "block", marginTop: 4 }}>
                    {fmtW(line.totalWeight)}
                  </span>
                </div>
              </div>
            </div>

            {/* Line comments */}
            <div>
              <h4 style={{ margin: "0 0 8px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Line Item Notes
              </h4>
              <div style={{
                padding: "12px 16px",
                background: "var(--pearl-bg, #fbf9f6)",
                border: "1px solid rgba(185,167,146,.2)",
                borderRadius: 10,
                fontSize: 13.5,
                color: line.comments ? "var(--dark)" : "var(--taupe)",
                fontStyle: line.comments ? "normal" : "italic",
                minHeight: 48
              }}>
                {line.comments || "No comments provided for this line item."}
              </div>
            </div>

            {/* Receipt Header Information */}
            <div>
              <h4 style={{ margin: "0 0 12px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Stock Receipt Header Information
              </h4>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: 12,
                padding: "16px",
                background: "white",
                borderRadius: 10,
                border: "1px solid rgba(185,167,146,.2)"
              }}>
                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Vendor</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>{vendorName}</div>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Receipt Date</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>{fmtDate(receipt.receiptDate)}</div>
                </div>

                <div>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>Receipt Reference</span>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--dark)", marginTop: 2 }}>Receipt #{receipt.receiptID}</div>
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 700, textTransform: "uppercase" }}>General Receipt Comments</span>
                  <div style={{ fontSize: 13, color: receipt.comments ? "var(--dark)" : "var(--taupe)", fontStyle: receipt.comments ? "normal" : "italic", marginTop: 4 }}>
                    {receipt.comments || "No general comments recorded."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
}

/* ══════════════════════════════════════
   MAIN STOCK RECEIPT PAGE
══════════════════════════════════════ */
export default function StockReceipt() {
  const [receipts, setReceipts] = useState([]);
  const [details, setDetails] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);

  const [tagModal, setTagModal] = useState(null);
  const [tagSaving, setTagSaving] = useState(false);
  const [printModal, setPrintModal] = useState(null);

  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 100, 200, 130, 220, 120]);

  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [rData, dData, vData, pData, iData] = await Promise.all([
        stockReceiptApi.getAll(),
        stockReceiptDtlApi.getAll(),
        vendorApi.getAll(),
        productApi.getAll(),
        inventoryItemDtlApi.getAll(),
      ]);
      setReceipts(Array.isArray(rData) ? rData : rData?.data ?? []);
      setDetails(Array.isArray(dData) ? dData : dData?.data ?? []);
      setVendors(Array.isArray(vData) ? vData : vData?.data ?? []);
      setProducts(Array.isArray(pData) ? pData : pData?.data ?? []);
      setInventoryItems(Array.isArray(iData) ? iData : iData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load stock receipts.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });
  const vendorMap = Object.fromEntries(vendors.map(v => [v.vendorID, v.vendorName]));

  /* ── Open edit modal with lines pre-loaded ── */
  const openEdit = (r) => {
    const existingLines = details.filter(d => d.receiptID === r.receiptID);
    setModal({ data: { ...r, lines: existingLines } });
  };

  /* ── Save ── */
  const handleSave = async ({ vendorID, receiptDate, comments, lines }) => {
    setSaving(true);
    try {
      let receiptID;

      if (modal?.data?.receiptID) {
        // Update header
        await stockReceiptApi.update({
          ...modal.data,
          vendorID: Number(vendorID),
          receiptDate,
          comments,
        });
        receiptID = modal.data.receiptID;

        // Update or create each line
        await Promise.all(lines.map(l =>
          l.receiptLineID
            ? stockReceiptDtlApi.update({
              receiptLineID: l.receiptLineID,
              receiptID,
              productID: Number(l.productID),
              quantity: Number(l.quantity),
              unitWeight: parseFloat(l.unitWeight) || 0,
              totalWeight: parseFloat(l.totalWeight) || 0,
              comments: l.comments || "",
            })
            : stockReceiptDtlApi.create({
              receiptID,
              productID: Number(l.productID),
              quantity: Number(l.quantity),
              unitWeight: parseFloat(l.unitWeight) || 0,
              totalWeight: parseFloat(l.totalWeight) || 0,
              comments: l.comments || "",
            })
        ));
        showToast("Stock receipt updated.");

      } else {
        // Create header
        const created = await stockReceiptApi.create({ vendorID: Number(vendorID), receiptDate, comments });
        receiptID = created?.receiptID ?? created?.data?.receiptID;
        if (!receiptID) throw new Error("No receiptID returned from server.");

        // Create all lines
        await Promise.all(lines.map(l =>
          stockReceiptDtlApi.create({
            receiptID,
            productID: Number(l.productID),
            quantity: Number(l.quantity),
            unitWeight: parseFloat(l.unitWeight) || 0,
            totalWeight: parseFloat(l.totalWeight) || 0,
            comments: l.comments || "",
          })
        ));
        showToast("Stock receipt saved successfully.");
      }
      setModal(null);
      emitDataMutation("stock_receipt");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Something went wrong.", "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!confirm) return;
    try {
      await stockReceiptApi.remove(confirm.id);
      showToast("Stock receipt deleted.");
      setConfirm(null);
      emitDataMutation("stock_receipt");
      fetchAll();
    } catch (err) {
      showToast(err.message || "Delete failed.", "error");
    }
  };

  /* ── Tag Handlers ── */
  const handleSaveTag = async (tagData) => {
    setTagSaving(true);
    try {
      const created = await inventoryItemDtlApi.create({
        receiptLineID: Number(tagData.receiptLineID),
        productID: Number(tagData.productID),
        grossWeight: parseFloat(tagData.grossWeight) || 0,
        lessWeight: parseFloat(tagData.lessWeight) || 0,
        netWeight: parseFloat(tagData.netWeight) || 0,
        quantity: Number(tagData.quantity) || 1,
        barcodeID: 0,
        stockID: 0,
      });
      const createdItem = created?.data ?? created;
      showToast("Jewelry tag created successfully.");
      setTagModal(null);
      emitDataMutation("stock");
      emitDataMutation("stock_receipt");
      await fetchAll();
      setPrintModal({
        item: createdItem?.inventoryItemID ? createdItem : {
          ...tagData,
          inventoryItemID: createdItem?.inventoryItemID || Date.now() % 100000,
        },
        product: products.find(p => p.productID === tagData.productID),
        vendors,
      });
    } catch (err) {
      showToast(err.message || "Failed to generate item tag.", "error");
    } finally {
      setTagSaving(false);
    }
  };

  const handleAutoTagAll = async (lines) => {
    const untagged = lines.filter(l => !inventoryItems.some(i => Number(i.receiptLineID) === Number(l.receiptLineID)));
    if (untagged.length === 0) {
      showToast("All line items are already tagged.");
      return;
    }
    setSaving(true);
    try {
      await Promise.all(untagged.map(l =>
        inventoryItemDtlApi.create({
          receiptLineID: Number(l.receiptLineID),
          productID: Number(l.productID),
          grossWeight: parseFloat(l.totalWeight) || 0,
          lessWeight: 0,
          netWeight: parseFloat(l.totalWeight) || 0,
          quantity: Number(l.quantity) || 1,
          barcodeID: 0,
          stockID: 0,
        })
      ));
      showToast(`Successfully created ${untagged.length} jewelry inventory tags.`);
      emitDataMutation("stock");
      emitDataMutation("stock_receipt");
      await fetchAll();
      const freshItems = await inventoryItemDtlApi.getAll();
      const freshList = Array.isArray(freshItems) ? freshItems : freshItems?.data ?? [];
      const receiptItems = freshList.filter(i => lines.some(l => Number(l.receiptLineID) === Number(i.receiptLineID)));
      setPrintModal({
        items: receiptItems,
        products,
        vendors,
      });
    } catch (err) {
      showToast(err.message || "Failed to auto-tag consignment items.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintTag = (item, product) => {
    setPrintModal({
      item,
      product: product || products.find(p => p.productID === item.productID),
      vendors,
    });
  };

  const handlePrintBatch = (items) => {
    setPrintModal({
      items,
      products,
      vendors,
    });
  };

  const fmt = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const filtered = receipts.filter(r => {
    const q = search.toLowerCase();
    return (
      String(r.receiptID).includes(q) ||
      (vendorMap[r.vendorID] ?? "").toLowerCase().includes(q) ||
      (r.comments ?? "").toLowerCase().includes(q)
    );
  });

  const { paged, page, totalPages, pageSize, setPage, setPageSize, pageStart, pageEnd, totalItems } =
    usePagination(filtered, 10);

  if (drawer) {
    return (
      <div className="rv-inv-page">
        <HubNav active="Stock Receipt" />
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        {modal && (
          <StockReceiptModal
            initial={modal.data}
            vendors={vendors}
            products={products}
            onSave={handleSave}
            onClose={() => setModal(null)}
            saving={saving}
          />
        )}
        {tagModal && (
          <TagGenerationModal
            line={tagModal.line}
            product={tagModal.product}
            receipt={tagModal.receipt}
            vendorName={tagModal.vendorName}
            onSave={handleSaveTag}
            onClose={() => setTagModal(null)}
            saving={tagSaving}
          />
        )}
        {printModal && (
          <BarcodeLabelPrinter
            item={printModal.item}
            items={printModal.items}
            product={printModal.product}
            products={products}
            vendors={vendors}
            onClose={() => setPrintModal(null)}
          />
        )}
        <StockReceiptDetailView
          receipt={drawer}
          vendors={vendors}
          products={products}
          allDetails={details}
          inventoryItems={inventoryItems}
          onBack={() => {
            setDrawer(null);
            const el = document.querySelector(".rv-content");
            if (el) el.scrollTop = 0;
            window.scrollTo(0, 0);
          }}
          onEdit={openEdit}
          onPrintTag={handlePrintTag}
          onGenerateTag={(line, prod, r) => setTagModal({ line, product: prod, receipt: r, vendorName: vendorMap[r.vendorID] })}
          onPrintBatch={handlePrintBatch}
          onAutoTagAll={handleAutoTagAll}
        />
      </div>
    );
  }

  return (
    <div className="rv-inv-page">
      <HubNav active="Stock Receipt" />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {modal && (
        <StockReceiptModal
          initial={modal.data}
          vendors={vendors}
          products={products}
          onSave={handleSave}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
      {confirm && (
        <ConfirmDialog
          name={`Receipt #${confirm.id}`}
          onConfirm={handleDelete}
          onCancel={() => setConfirm(null)}
        />
      )}
      {tagModal && (
        <TagGenerationModal
          line={tagModal.line}
          product={tagModal.product}
          receipt={tagModal.receipt}
          vendorName={tagModal.vendorName}
          onSave={handleSaveTag}
          onClose={() => setTagModal(null)}
          saving={tagSaving}
        />
      )}
      {printModal && (
        <BarcodeLabelPrinter
          item={printModal.item}
          items={printModal.items}
          product={printModal.product}
          products={products}
          vendors={vendors}
          onClose={() => setPrintModal(null)}
        />
      )}

      {/* Topbar */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left">
          <h2 className="rv-inv-heading">Stock Receipts</h2>
          <span className="rv-inv-count">{receipts.length} total</span>
        </div>
        <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
          <PlusIcon /> New Receipt
        </button>
      </div>

      {/* Filters */}
      <div className="rv-inv-filters">
        <div className="rv-inv-search">
          <SearchIcon />
          <input
            placeholder="Search by receipt ID, vendor, or comments…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Table card */}
      <div className="rv-card rv-inv-card">
        {loading ? (
          <div className="rv-inv-empty">
            <div className="rv-loader" />
            <p>Loading stock receipts…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rv-inv-empty">
            <div className="rv-empty-icon"><ReceiptIcon /></div>
            <p className="rv-empty-title">No stock receipts found</p>
            <p className="rv-empty-sub">
              {search ? "Try a different search term." : "Record your first vendor delivery to get started."}
            </p>
            {!search && (
              <button className="rv-btn rv-btn--primary rv-mt-12" onClick={() => setModal({ data: {} })}>
                <PlusIcon /> New Receipt
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="rv-desktop-table-view">
              <div className="rv-table-wrap rv-inv-table-wrap">
                <table className="rv-table rv-inv-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Receipt ID</th>
                      <th>Vendor</th>
                      <th>Date</th>
                      <th>Comments</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((r, i) => (
                      <tr
                        key={r.receiptID}
                        className="rv-inv-row"
                        onClick={() => setDrawer(r)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-inv-idx">{pageStart + i}</td>
                        <td><span className="rv-invitem-id-chip">#{r.receiptID}</span></td>
                        <td><span className="rv-inv-product-name">{vendorMap[r.vendorID] ?? `Vendor #${r.vendorID}`}</span></td>
                        <td>{fmt(r.receiptDate)}</td>
                        <td>
                          <span style={{ fontSize: 12.5, color: "var(--taupe)" }}>
                            {r.comments
                              ? (r.comments.length > 40 ? r.comments.slice(0, 40) + "…" : r.comments)
                              : <span className="rv-empty-val">—</span>}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="rv-actions">
                            <button
                              className="rv-action-btn rv-action-btn--edit"
                              title="Edit"
                              onClick={() => openEdit(r)}
                            ><EditIcon /></button>
                            <button
                              className="rv-action-btn rv-action-btn--del"
                              title="Delete"
                              onClick={() => setConfirm({ id: r.receiptID })}
                            ><TrashIcon /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Cards View */}
            <div className="rv-mobile-cards-view">
              <RvDataCardList
                loading={loading}
                items={paged}
                emptyIcon={<ReceiptIcon />}
                emptyTitle="No stock receipts found"
                emptySubtitle={search ? "Try a different search term." : "Record your first vendor delivery to get started."}
                emptyAction={
                  !search && (
                    <button className="rv-btn rv-btn--primary" onClick={() => setModal({ data: {} })}>
                      <PlusIcon /> New Receipt
                    </button>
                  )
                }
                renderCard={(r, i) => (
                  <RvDataCard
                    key={r.receiptID}
                    index={pageStart + i}
                    title={vendorMap[r.vendorID] ?? `Vendor #${r.vendorID}`}
                    subtitle={`Receipt #${r.receiptID}`}
                    badge={<span className="rv-invitem-id-chip">#{r.receiptID}</span>}
                    fields={[
                      { label: "Delivery Date", value: fmt(r.receiptDate) },
                      { label: "Vendor", value: vendorMap[r.vendorID] ?? `Vendor #${r.vendorID}` },
                      { label: "Comments", value: r.comments || "—", fullWidth: true },
                    ]}
                    onClick={() => setDrawer(r)}
                    actions={
                      <div className="rv-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="rv-action-btn rv-action-btn--edit"
                          title="Edit"
                          onClick={() => openEdit(r)}
                        ><EditIcon /></button>
                        <button
                          className="rv-action-btn rv-action-btn--del"
                          title="Delete"
                          onClick={() => setConfirm({ id: r.receiptID })}
                        ><TrashIcon /></button>
                      </div>
                    }
                  />
                )}
              />
            </div>

            {/* Shared Pagination */}
            <Pagination
              page={page} totalPages={totalPages} pageSize={pageSize}
              setPage={setPage} setPageSize={setPageSize}
              pageStart={pageStart} pageEnd={pageEnd} totalItems={totalItems}
            />
          </>
        )}
      </div>

      {!loading && filtered.length > 0 && (
        <p className="rv-resize-hint rv-desktop-table-view"><GripIcon /> Drag column borders to resize · Click a row to view details</p>
      )}
    </div>
  );
}

/* ── Icons ── */
function PlusIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>; }
function SearchIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>; }
function EditIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>; }
function TrashIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>; }
function CloseIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>; }
function CheckIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>; }
function AlertIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>; }
function GripIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" style={{ width: 13, height: 13 }}><circle cx="9" cy="5" r="1" fill="currentColor" /><circle cx="9" cy="12" r="1" fill="currentColor" /><circle cx="9" cy="19" r="1" fill="currentColor" /><circle cx="15" cy="5" r="1" fill="currentColor" /><circle cx="15" cy="12" r="1" fill="currentColor" /><circle cx="15" cy="19" r="1" fill="currentColor" /></svg>; }
function ReceiptIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>; }
function TagIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>; }
function PrintIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>; }
function SparklesIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" /></svg>; }
function BoxIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /></svg>; }
function TxnIcon() { return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" width="14" height="14"><path d="M7 16V4m0 0L3 8m4-4l4 4m6 4v12m0 0l4-4m-4 4l-4-4" /></svg>; }